import type { Api } from "@ap/api";
import type { SessionUser } from "@ap/ui-kit";
import { useEffect, useMemo, useState } from "react";
import { useApi } from "./api";

/**
 * "Needs you" sources (ADR-0055): things waiting for the signed-in user, counted from EXISTING
 * read endpoints only. Each source declares the roles that can act on it; others never call it.
 * Add a source here and the bell and the Home page show it.
 */
export interface NeedSource {
  key: string;
  /** Any of these realm roles. */
  roles: string[];
  /** Where the one primary action goes. */
  path: string;
  /** Glossary term for the card. */
  term?: string;
  /** Only for people of a customer tenant: the platform team (tenant _platform) has no such list. */
  tenantOnly?: boolean;
  count: (api: Api, user: SessionUser) => Promise<number>;
}

const ACTIVE_CASE = new Set(["open", "pending", "escalated"]);

/** The signed-in person (ids are the subject; some services store the username). */
const isMe = (id: string | null | undefined, u: SessionUser) => !!id && (id === u.sub || id === u.username);

export const NEED_SOURCES: NeedSource[] = [
  { key: "inbox", roles: ["reviewer", "approver", "finance-approver"], path: "/inbox", term: "task", count: async (api, u) => (await api.cases.inbox()).filter((i) => !i.task.assignee || isMe(i.task.assignee, u)).length },
  {
    key: "cases",
    roles: ["case-worker", "case-supervisor", "case-admin"],
    path: "/cases",
    term: "case",
    // Only cases assigned to this person: the bell counts what needs *them*, not every open case.
    count: async (api, u) => (await api.cases.list()).filter((c) => ACTIVE_CASE.has(c.status) && isMe(c.assignee, u)).length,
  },
  {
    key: "desk",
    roles: ["channel-agent", "channel-supervisor"],
    path: "/channels?tab=desk",
    term: "handover",
    // The desk is per tenant: a platform-team token is refused ("a tenant-scoped token is required").
    tenantOnly: true,
    // Conversations an agent handed to a person and nobody has closed yet.
    count: async (api) => (await api.channels.desk()).length,
  },
  {
    key: "promotions",
    roles: ["release-approver"],
    path: "/governance?tab=releases",
    term: "promotion",
    count: async (api, u) => (await api.controlPlane.promotions(u.tenant)).filter((p) => p.status === "pending_approval" && !isMe(p.requested_by, u) && !(p.approvals ?? []).some((a) => isMe(a.approver, u))).length,
  },
  {
    key: "budgets",
    roles: ["operator", "finance", "tenant-admin"],
    path: "/health?tab=details",
    term: "budget",
    count: async (api) => (await api.metering.budgets()).filter((b) => b.soft_exceeded || b.utilisation >= 1).length,
  },
  {
    key: "certifications",
    roles: ["marketplace-curator"],
    path: "/governance?tab=marketplace",
    term: "certification",
    count: async (api) => (await api.controlPlane.listings()).filter((l) => l.status === "published" && l.certification === "uncertified").length,
  },
  {
    key: "globalKill",
    roles: ["kill-switch-operator", "platform-admin"],
    path: "/governance?tab=killswitch",
    term: "killSwitch",
    count: async (api) => (await api.controlPlane.globalKillRequests()).length,
  },
];

/**
 * For every signed-in person, whatever their roles, and shown only while something is waiting:
 * notifications agents sent to them or their teams (notify tool, ADR-0091).
 */
export const PERSONAL_SOURCES: NeedSource[] = [
  { key: "notifications", roles: [], path: "/notifications", count: async (api) => (await api.notify.unreadCount()).unread },
];

export function needSourcesFor(roles: readonly string[], tenant?: string): NeedSource[] {
  return NEED_SOURCES.filter((s) => s.roles.some((r) => roles.includes(r)) && !(s.tenantOnly && tenant === "_platform"));
}

export interface Need {
  source: NeedSource;
  /** null while loading; -1 when the count could not be read. */
  count: number | null;
}

/** Counts for every source the user can act on. Refreshes every `refreshMs` (default 60 s). */
export function useNeedsYou(user: SessionUser | null, refreshMs = 60_000): { needs: Need[]; total: number; loading: boolean } {
  const api = useApi();
  const sources = useMemo(() => [...needSourcesFor(user?.roles ?? [], user?.tenant), ...PERSONAL_SOURCES], [user]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (refreshMs <= 0) return;
    const id = setInterval(() => setTick((x) => x + 1), refreshMs);
    return () => clearInterval(id);
  }, [refreshMs]);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    for (const s of sources) {
      s.count(api, user).then(
        (n) => alive && setCounts((c) => ({ ...c, [s.key]: n })),
        () => alive && setCounts((c) => ({ ...c, [s.key]: -1 })),
      );
    }
    return () => {
      alive = false;
    };
  }, [api, user, sources, tick]);
  const personal = new Set(PERSONAL_SOURCES.map((s) => s.key));
  const needs = sources
    .map((source) => ({ source, count: counts[source.key] ?? null }))
    .filter((n) => !personal.has(n.source.key) || (n.count !== null && n.count > 0));
  const total = needs.reduce((n, x) => n + (x.count && x.count > 0 ? x.count : 0), 0);
  return { needs, total, loading: needs.some((x) => x.count === null) };
}
